import { Data } from "rete/types/core/data"
import {
  CATEGORICAL_TO_NUMERIC_COMPONENT_NAME,
  catMapNumericValueString,
  normalizeCatMapLabelRows,
} from "../modelling/components/catergorical_to_numeric_component"
import { PAYOFF_MATRIX_COMPONENT_NAME } from "../modelling/components/boolean_payoff_matrix_component"
import {
  BAR_CHART_COMPONENT_NAME,
  BAR_CHART_VARIABLES_KEY,
} from "../modelling/components/bar_chart_component"
import {
  SANKEY_COMPONENT_NAME,
  SANKEY_VARIABLES_KEY,
} from "../modelling/components/sankey_component"
import {
  BAR_DISTRIBUTION_CHART_COMPONENT_NAME,
  HISTOGRAM_CHART_COMPONENT_NAME,
  PIE_CHART_COMPONENT_NAME,
  SPIDER_CHART_COMPONENT_NAME,
  TABLE_CHART_COMPONENT_NAME,
  TABLE_COMPARE_CHART_COMPONENT_NAME,
} from "../modelling/components/dataset_chart_component"
import {
  DATASET_CHART_KEY,
  SPIDER_CHART_KEY,
  TABLE_CHART_KEY,
  TABLE_COMPARE_CHART_KEY,
  readDatasetChart,
  readSpiderChart,
  readTableChart,
  readTableCompareChart,
  spiderChartHasData,
  tableCompareDifference,
  type DatasetChartKind,
  type SerializedDatasetChart,
  type SerializedSpiderChart,
  type SerializedTableChart,
  type SerializedTableCompareChart,
} from "../modelling/dataset_chart_data"
import type { BarChartVariable } from "../modelling/controls/barchart"
import {
  deserializeSankeyVariables,
  type SankeyStageTransition,
} from "../modelling/controls/sankey"
import {
  NEW_TABLE_COMPONENT_NAME,
  TABLE_CELLS_KEY,
  TABLE_COLUMNS_KEY,
  TABLE_ROWS_KEY,
} from "../modelling/controls/table_editor"
import type { TableCell } from "../modelling/table_spreadsheet"
import {
  payoffFromNodeData,
  readPayoffAxesFromNodeData,
  type PayoffAxesConfig,
} from "../modelling/payoff_matrix_logic"

export const NUMERIC_CONSTANT_COMPONENT_NAME = "Numeric constant"
export {
  CATEGORICAL_TO_NUMERIC_COMPONENT_NAME,
  PAYOFF_MATRIX_COMPONENT_NAME,
  BAR_CHART_COMPONENT_NAME,
  SANKEY_COMPONENT_NAME,
  PIE_CHART_COMPONENT_NAME,
  BAR_DISTRIBUTION_CHART_COMPONENT_NAME,
  HISTOGRAM_CHART_COMPONENT_NAME,
  TABLE_CHART_COMPONENT_NAME,
  TABLE_COMPARE_CHART_COMPONENT_NAME,
  SPIDER_CHART_COMPONENT_NAME,
}

export type NumericConstantDetailEntry = {
  nodeId: number
  name: string
  value: string
}

type ModelNodeInput = {
  connections?: unknown[]
}

type ModelNode = {
  id: number
  name: string
  data?: Record<string, unknown>
  inputs?: Record<string, ModelNodeInput>
}

export function catValInputKeyForCategoryValue(categoryValue: number): string {
  return `catVal_${categoryValue}`
}

export function catValInputHasConnection(node: ModelNode, categoryValue: number): boolean {
  const input = node.inputs?.[catValInputKeyForCategoryValue(categoryValue)]
  return (input?.connections?.length ?? 0) > 0
}

export function isNumericConstantNode(node: ModelNode): boolean {
  return node.name === NUMERIC_CONSTANT_COMPONENT_NAME
}

export function numericConstantDisplayName(node: ModelNode): string {
  const custom = node.data?.name
  if (typeof custom === "string" && custom.trim() !== "") {
    return custom
  }
  return node.name
}

export function numericConstantValueString(node: ModelNode): string {
  const value = node.data?.Value
  if (typeof value === "number" && !Number.isNaN(value)) {
    return String(value)
  }
  if (typeof value === "string") {
    return value
  }
  return "0"
}

export function isNumericConstantExposed(node: ModelNode): boolean {
  const expose = node.data?.expose
  if (expose === true || expose === "true" || expose === 1) {
    return true
  }
  const legacy = node.data?.showInDetail
  return legacy === true || legacy === "true" || legacy === 1
}

export function isNodeExposed(node: ModelNode): boolean {
  const expose = node.data?.expose
  if (expose === true || expose === "true" || expose === 1) {
    return true
  }
  const legacy = node.data?.showInDetail
  return legacy === true || legacy === "true" || legacy === 1
}

export function isCategoricalToNumericNode(node: ModelNode): boolean {
  return node.name === CATEGORICAL_TO_NUMERIC_COMPONENT_NAME
}

export type CategoricalMapDetailRow = {
  categoryValue: number
  label: string
  numericValue: string
  socketConnected: boolean
}

export type CategoricalToNumericDetailEntry = {
  nodeId: number
  name: string
  rows: CategoricalMapDetailRow[]
}

export function categoricalToNumericDisplayName(node: ModelNode): string {
  const custom = node.data?.name
  if (typeof custom === "string" && custom.trim() !== "") {
    return custom
  }
  return node.name
}

export const DETAIL_GROUP_BY_CATEGORY_STORAGE_KEY = "landscapes.modelDetail.groupByCategory"

export function readDetailGroupByCategory(): boolean {
  try {
    const stored = localStorage.getItem(DETAIL_GROUP_BY_CATEGORY_STORAGE_KEY)
    if (stored === "false") {
      return false
    }
    return true
  } catch {
    return true
  }
}

export function writeDetailGroupByCategory(value: boolean): void {
  try {
    localStorage.setItem(DETAIL_GROUP_BY_CATEGORY_STORAGE_KEY, value ? "true" : "false")
  } catch {
    // ignore quota / private mode
  }
}

/**
 * A single underlying node + its category value for one matrix cell. When
 * multiple `Categorical to numeric dataset` nodes share the same display
 * name we merge them into a single column (see `buildCategoricalDetailMatrix`),
 * but writes still need to land on every individual node — hence one target
 * per sibling.
 */
export type CategoricalDetailCellTarget = {
  nodeId: number
  /**
   * Category value on *this* sibling node — sibling nodes can have different
   * numeric category values for the same label (one may store "Forest" as
   * 2, another as 5). So the cell holds a separate `categoryValue` per
   * target rather than a single shared value.
   */
  categoryValue: number
  numericValue: string
  socketConnected: boolean
}

export type CategoricalDetailComponentRow = {
  /**
   * First sibling's nodeId. Retained so existing consumers that only need a
   * "representative" id (e.g. legacy code paths) keep working. **Writes
   * must iterate `targets` rather than using this single id.**
   */
  nodeId: number
  componentName: string
  categoryValue: number
  /** Display value (first target's value). */
  numericValue: string
  /** True iff *any* target's input is socket-driven; we disable editing in that case. */
  socketConnected: boolean
  /**
   * Every node sharing this column-cell. When the column represents a
   * single component this is a 1-element array; when multiple components
   * share a name it lists all sibling targets.
   */
  targets: CategoricalDetailCellTarget[]
  /**
   * True when sibling targets store different numeric values for this
   * category. Lets the UI flag "linked but currently out of sync" so the
   * user knows editing will pull them back together.
   */
  valuesAreMixed: boolean
}

export type CategoricalDetailCategoryGroup = {
  label: string
  groupKey: string
  rows: CategoricalDetailComponentRow[]
}

/** Normalized key for matching category labels (map probe ↔ detail panel). */
export function categoryGroupKeyForLabel(label: string): string {
  return label.trim().toLowerCase()
}

/** Normalized key for matching column names (component display name). */
function columnKeyForName(name: string): string {
  return name.trim().toLowerCase()
}

export type CategoricalDetailMatrixColumn = {
  /** Stable React key — unique within a matrix; normalized component name. */
  key: string
  /** Display label shown in the column header (verbatim component name). */
  componentName: string
  /**
   * All node IDs whose display name maps to this column. Length 1 in the
   * common case; length > 1 when the user has named several components the
   * same, which is the "link these together" gesture this matrix honours.
   */
  nodeIds: number[]
}

export type CategoricalDetailMatrixRow = {
  label: string
  /** Stable React/CSV key — unique per matrix row, including sub-rows. */
  groupKey: string
  /**
   * When this row was split out because of a per-cell value conflict in a
   * merged column, `siblingNodeId` identifies which sibling's value
   * this row carries for the conflicting cell(s). `undefined` for the
   * non-split common case. The Detail-panel UI shows a small "#id"
   * annotation beneath the row label so the user can tell sub-rows apart.
   */
  siblingNodeId?: number
  cells: (CategoricalDetailComponentRow | null)[]
}

export type CategoricalDetailMatrix = {
  columns: CategoricalDetailMatrixColumn[]
  rows: CategoricalDetailMatrixRow[]
}

type EntryRow = CategoricalToNumericDetailEntry["rows"][number]

/**
 * Parse a saved cell value string into a finite number, or `null` when the
 * cell is "uninitialized" — empty / unparseable / NaN. The
 * row-split-on-conflict logic treats `null` and `0` identically (see
 * `isMeaningfulNonZeroValue`), but exposing `null` keeps the helper
 * reusable for any future caller that needs to tell "user explicitly set
 * 0" apart from "user hasn't touched this yet".
 */
function parseSavedNumeric(value: string): number | null {
  if (value === undefined || value === null) return null
  const trimmed = String(value).trim()
  if (trimmed === "") return null
  const n = Number(trimmed)
  if (!Number.isFinite(n)) return null
  return n
}

/**
 * A "meaningful" value is anything the user has gone out of their way to
 * set to a non-zero number. Zeros, blanks and NaNs are treated as
 * "uninitialized": they don't trigger a split when paired with a sibling
 * that has a real value, since editing in the merged cell will simply pull
 * the unset siblings into sync.
 */
function isMeaningfulNonZeroValue(value: string): boolean {
  const n = parseSavedNumeric(value)
  return n !== null && n !== 0
}

/**
 * `true` when at least two of the supplied contributions hold
 * meaningfully-non-zero numeric values that *differ* — i.e. the user has
 * gone out of their way to set the same (label, component-name) cell to
 * different real values across sibling nodes. That's the "they're not
 * actually the same parameter, just named the same" signal that triggers
 * the per-label row split below.
 *
 * Pure zeros / blanks / NaN don't count as "real intent" (same reasoning
 * as `isMeaningfulNonZeroValue` above), so 5-vs-0 still merges and just
 * gets flagged as `valuesAreMixed`.
 */
function cellTargetsConflict(
  contributions: Array<{ entry: CategoricalToNumericDetailEntry; row: EntryRow }>
): boolean {
  if (contributions.length < 2) return false
  const distinctNonZero = new Set<number>()
  for (const { row } of contributions) {
    if (!isMeaningfulNonZeroValue(row.numericValue)) continue
    distinctNonZero.add(parseSavedNumeric(row.numericValue) as number)
    if (distinctNonZero.size > 1) return true
  }
  return false
}

/**
 * Build one merged cell from every sibling `(entry, row)` pair that
 * contributes to a given (column, label) position. Returns `null` when no
 * sibling has a matching label, which is what `cells: (... | null)[]`
 * encodes in `CategoricalDetailMatrixRow`.
 */
function mergeCellTargets(
  componentName: string,
  contributions: Array<{ entry: CategoricalToNumericDetailEntry; row: EntryRow }>
): CategoricalDetailComponentRow | null {
  if (contributions.length === 0) {
    return null
  }
  const targets: CategoricalDetailCellTarget[] = contributions
    .map(({ entry, row }) => ({
      nodeId: entry.nodeId,
      categoryValue: row.categoryValue,
      numericValue: row.numericValue,
      socketConnected: row.socketConnected,
    }))
    // Stable ordering: lowest nodeId first so the "representative" value is
    // deterministic across renders even though Map iteration order is also
    // insertion order today.
    .sort((a, b) => a.nodeId - b.nodeId)

  const first = targets[0]
  const valuesAreMixed = targets.some(t => t.numericValue !== first.numericValue)
  const anySocketConnected = targets.some(t => t.socketConnected)

  return {
    nodeId: first.nodeId,
    componentName,
    categoryValue: first.categoryValue,
    numericValue: first.numericValue,
    socketConnected: anySocketConnected,
    targets,
    valuesAreMixed,
  }
}

/**
 * Pivot: category labels as rows, exposed components as columns.
 *
 * **Column merging by component name:** when several `Categorical to
 * numeric dataset` nodes share the same display name (case- and
 * whitespace-insensitive), they collapse into a single column whose cell
 * writes fan out to every sibling node. The column header still shows the
 * shared name, and `column.nodeIds` records the underlying ids so the
 * matrix CSV import and Detail-panel UI can emit one write per target.
 *
 * **Per-label row split on conflict:** when a particular (label, merged
 * column) cell holds two or more *different non-zero* numeric values
 * across sibling nodes, that label gets split into one row per
 * contributing sibling. Each sub-row:
 *   - shows that sibling's own value in the conflicting column(s);
 *   - shows "—" in non-conflicting columns where that sibling has no
 *     contribution; or, where the sibling *does* contribute to a
 *     non-conflicting column, shows the standard merged cell (because
 *     editing it should still fan out across all siblings sharing that
 *     non-conflicting column).
 *
 * The split is purely a presentation rebucketing — same node data
 * underneath, so editing a sub-row's cell still hits exactly the sibling
 * node(s) it represents.
 */
export function buildCategoricalDetailMatrix(
  entries: CategoricalToNumericDetailEntry[]
): CategoricalDetailMatrix {
  // 1. Build merged columns (one per normalized display name). Entries
  //    are pre-sorted by ascending node id (see
  //    `getCategoricalToNumericDetailEntries`), so the first occurrence
  //    of a given name fixes that column's place in the matrix.
  const columnIdxByNameKey = new Map<string, number>()
  const columns: CategoricalDetailMatrixColumn[] = []
  for (const entry of entries) {
    const nameKey = columnKeyForName(entry.name)
    const existing = columnIdxByNameKey.get(nameKey)
    if (existing === undefined) {
      columnIdxByNameKey.set(nameKey, columns.length)
      columns.push({ key: nameKey, componentName: entry.name, nodeIds: [entry.nodeId] })
    } else {
      columns[existing].nodeIds.push(entry.nodeId)
    }
  }

  // 2. Pivot raw contributions into `(label, columnIdx) → list of (entry, row)`.
  type Contribution = { entry: CategoricalToNumericDetailEntry; row: EntryRow }
  const contributionsByLabelKey = new Map<
    string,
    { label: string; perColumn: Map<number, Contribution[]> }
  >()
  for (const entry of entries) {
    const columnIdx = columnIdxByNameKey.get(columnKeyForName(entry.name))
    if (columnIdx === undefined) continue
    for (const row of entry.rows) {
      const rowKey = categoryGroupKeyForLabel(row.label)
      let bucket = contributionsByLabelKey.get(rowKey)
      if (!bucket) {
        bucket = { label: row.label, perColumn: new Map() }
        contributionsByLabelKey.set(rowKey, bucket)
      }
      const colBucket = bucket.perColumn.get(columnIdx) ?? []
      colBucket.push({ entry, row })
      bucket.perColumn.set(columnIdx, colBucket)
    }
  }

  // 3. Emit matrix rows. Most labels yield exactly one row; a label whose
  //    (label, column) cells include any conflict yields N sub-rows,
  //    where N is the number of distinct sibling node ids across the
  //    conflicting columns (so that *every* contributing component gets
  //    its own row, as the user expects).
  const rows: CategoricalDetailMatrixRow[] = []
  const sortedByLabel = Array.from(contributionsByLabelKey.entries()).sort(
    ([, a], [, b]) => a.label.localeCompare(b.label)
  )

  for (const [labelKey, { label, perColumn }] of sortedByLabel) {
    const conflictingColumnIdxs: number[] = []
    for (const [colIdx, contribs] of Array.from(perColumn.entries())) {
      if (cellTargetsConflict(contribs)) {
        conflictingColumnIdxs.push(colIdx)
      }
    }

    if (conflictingColumnIdxs.length === 0) {
      // No cell conflict at this label — single merged row, same as before.
      const cells = columns.map((col, idx) =>
        mergeCellTargets(col.componentName, perColumn.get(idx) ?? [])
      )
      rows.push({ label, groupKey: labelKey, cells })
      continue
    }

    // Build the ordered sibling list that drives the sub-rows. We union
    // contributions across every conflicting column so that *all*
    // components that touch the disputed label get their own row, even
    // when two different merged columns disagree on the same label with
    // different sibling sets.
    const siblingNodeIds = new Set<number>()
    for (const colIdx of conflictingColumnIdxs) {
      for (const { entry } of perColumn.get(colIdx) ?? []) {
        siblingNodeIds.add(entry.nodeId)
      }
    }
    const orderedSiblingIds = Array.from(siblingNodeIds).sort((a, b) => a - b)

    for (const siblingNodeId of orderedSiblingIds) {
      const cells = columns.map((col, idx) => {
        const contribs = perColumn.get(idx) ?? []
        if (conflictingColumnIdxs.includes(idx)) {
          // Restrict to this sibling's own contribution; if it has none,
          // render an empty cell (the `null` placeholder) so the sub-row
          // visually shows the absence rather than a stale neighbour value.
          const own = contribs.filter(c => c.entry.nodeId === siblingNodeId)
          return mergeCellTargets(col.componentName, own)
        }
        // Non-conflicting column: re-show the same merged cell on every
        // sub-row. Editing it still fans out across the column's full
        // target set (the non-conflicting siblings sharing this name),
        // which is the desired "the value here applies to everyone"
        // behaviour for the column.
        return mergeCellTargets(col.componentName, contribs)
      })
      rows.push({
        label,
        groupKey: `${labelKey}::sib-${siblingNodeId}`,
        siblingNodeId,
        cells,
      })
    }
  }

  return { columns, rows }
}

/**
 * Legacy: walk entries grouped by category label, with one row per
 * underlying entry node (no column merging by component name — that lives
 * in `buildCategoricalDetailMatrix`). Kept for any external consumer that
 * imports the function; internal Detail-panel rendering uses the matrix.
 */
export function groupCategoricalDetailByCategory(
  entries: CategoricalToNumericDetailEntry[]
): CategoricalDetailCategoryGroup[] {
  const groups = new Map<string, CategoricalDetailCategoryGroup>()

  for (const entry of entries) {
    for (const row of entry.rows) {
      const groupKey = categoryGroupKeyForLabel(row.label)
      let group = groups.get(groupKey)
      if (!group) {
        group = { label: row.label, groupKey, rows: [] }
        groups.set(groupKey, group)
      }
      const target: CategoricalDetailCellTarget = {
        nodeId: entry.nodeId,
        categoryValue: row.categoryValue,
        numericValue: row.numericValue,
        socketConnected: row.socketConnected,
      }
      group.rows.push({
        nodeId: entry.nodeId,
        componentName: entry.name,
        categoryValue: row.categoryValue,
        numericValue: row.numericValue,
        socketConnected: row.socketConnected,
        targets: [target],
        valuesAreMixed: false,
      })
    }
  }

  return Array.from(groups.values())
    .map(group => ({
      ...group,
      rows: group.rows.slice().sort((a, b) => a.componentName.localeCompare(b.componentName)),
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

export function getCategoricalToNumericDetailEntries(model: Data | null): CategoricalToNumericDetailEntry[] {
  if (model?.nodes === undefined) {
    return []
  }

  return Object.values(model.nodes as Record<string, ModelNode>)
    .filter(node => isCategoricalToNumericNode(node) && isNodeExposed(node))
    .sort((a, b) => a.id - b.id)
    .map(node => {
      const data = node.data ?? {}
      const rows = normalizeCatMapLabelRows(data.catMapLabels).map(row => ({
        categoryValue: row.value,
        label: row.label,
        numericValue: catMapNumericValueString(data, row.value),
        socketConnected: catValInputHasConnection(node, row.value),
      }))
      return {
        nodeId: node.id,
        name: categoricalToNumericDisplayName(node),
        rows,
      }
    })
    .filter(entry => entry.rows.length > 0)
}

export function isPayoffMatrixNode(node: ModelNode): boolean {
  return node.name === PAYOFF_MATRIX_COMPONENT_NAME
}

export function payoffMatrixDisplayName(node: ModelNode): string {
  const custom = node.data?.name
  if (typeof custom === "string" && custom.trim() !== "") {
    return custom
  }
  return node.name
}

export type PayoffMatrixDetailEntry = {
  nodeId: number
  name: string
  axes: PayoffAxesConfig
}

export function payoffMatrixCellValueString(
  data: Record<string, unknown>,
  aValue: number,
  bValue: number,
  axes?: PayoffAxesConfig
): string {
  const axesConfig = axes ?? readPayoffAxesFromNodeData(data)
  const value = payoffFromNodeData(data, aValue, bValue, axesConfig)
  if (Number.isNaN(value)) {
    return ""
  }
  return String(value)
}

export function getPayoffMatrixDetailEntries(model: Data | null): PayoffMatrixDetailEntry[] {
  if (model?.nodes === undefined) {
    return []
  }

  return Object.values(model.nodes as Record<string, ModelNode>)
    .filter(node => isPayoffMatrixNode(node) && isNodeExposed(node))
    .sort((a, b) => a.id - b.id)
    .map(node => {
      const data = node.data ?? {}
      const axes = readPayoffAxesFromNodeData(data)
      return {
        nodeId: node.id,
        name: payoffMatrixDisplayName(node),
        axes,
      }
    })
    .filter(entry => entry.axes.a.length > 0 && entry.axes.b.length > 0)
}

export function isNewTableNode(node: ModelNode): boolean {
  return node.name === NEW_TABLE_COMPONENT_NAME
}

export function tableDisplayName(node: ModelNode): string {
  const custom = node.data?.name
  if (typeof custom === "string" && custom.trim() !== "") {
    return custom
  }
  return node.name
}

function readTableStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.map(v => String(v)) : []
}

function normalizeTableCells(rows: string[], columns: string[], raw: unknown): TableCell[][] {
  const existing = Array.isArray(raw) ? (raw as TableCell[][]) : []
  return rows.map((_r, r) =>
    columns.map((_c, c) => {
      const value = existing[r] ? existing[r][c] : null
      return typeof value === "number" && Number.isFinite(value) ? value : null
    })
  )
}

export function isTableColumnsLocked(node: ModelNode): boolean {
  const ref = node.inputs?.ref
  return (ref?.connections?.length ?? 0) > 0
}

export type TableDetailEntry = {
  nodeId: number
  name: string
  rows: string[]
  columns: string[]
  cells: TableCell[][]
  columnsLocked: boolean
}

export function tableCellValueString(cells: TableCell[][], rowIndex: number, columnIndex: number): string {
  const value = cells[rowIndex] ? cells[rowIndex][columnIndex] : null
  return typeof value === "number" && Number.isFinite(value) ? String(value) : ""
}

export function readTableDetailFromNode(node: ModelNode): Omit<TableDetailEntry, "nodeId" | "name"> {
  const data = node.data ?? {}
  const rows = readTableStringArray(data[TABLE_ROWS_KEY])
  const columns = readTableStringArray(data[TABLE_COLUMNS_KEY])
  return {
    rows,
    columns,
    cells: normalizeTableCells(rows, columns, data[TABLE_CELLS_KEY]),
    columnsLocked: isTableColumnsLocked(node),
  }
}

export function getTableDetailEntries(model: Data | null): TableDetailEntry[] {
  if (model?.nodes === undefined) {
    return []
  }

  return Object.values(model.nodes as Record<string, ModelNode>)
    .filter(node => isNewTableNode(node) && isNodeExposed(node))
    .sort((a, b) => a.id - b.id)
    .map(node => ({
      nodeId: node.id,
      name: tableDisplayName(node),
      ...readTableDetailFromNode(node),
    }))
}

export function getTableDetailEntry(model: Data | null, nodeId: number): TableDetailEntry | null {
  if (model?.nodes === undefined) {
    return null
  }
  const nodes = model.nodes as Record<string, ModelNode>
  const node = nodes[String(nodeId)] ?? nodes[nodeId]
  if (!node || !isNewTableNode(node)) {
    return null
  }
  return {
    nodeId: node.id,
    name: tableDisplayName(node),
    ...readTableDetailFromNode(node),
  }
}

export type TableProbeMatch = {
  nodeId: number
  tableName: string
  columnIndex: number
  columnLabel: string
  rows: Array<{ rowLabel: string; value: string }>
}

/** Group keys for every exposed table column (map probe ↔ detail panel). */
export function availableTableColumnGroupKeys(model: Data | null): Set<string> {
  const keys = new Set<string>()
  for (const entry of getTableDetailEntries(model)) {
    for (const column of entry.columns) {
      keys.add(categoryGroupKeyForLabel(column))
    }
  }
  return keys
}

/** Exposed tables whose column label matches the probed categorical class. */
export function getTableProbeMatches(
  model: Data | null,
  categoryGroupKey: string | null
): TableProbeMatch[] {
  if (!categoryGroupKey || model?.nodes === undefined) {
    return []
  }

  return getTableDetailEntries(model)
    .map(entry => {
      const columnIndex = entry.columns.findIndex(
        column => categoryGroupKeyForLabel(column) === categoryGroupKey
      )
      if (columnIndex < 0) {
        return null
      }
      return {
        nodeId: entry.nodeId,
        tableName: entry.name,
        columnIndex,
        columnLabel: entry.columns[columnIndex],
        rows: entry.rows.map((rowLabel, rowIndex) => ({
          rowLabel,
          value: tableCellValueString(entry.cells, rowIndex, columnIndex) || "—",
        })),
      }
    })
    .filter((match): match is TableProbeMatch => match !== null)
}

export function getNumericConstantDetailEntries(model: Data | null): NumericConstantDetailEntry[] {
  if (model?.nodes === undefined) {
    return []
  }

  return Object.values(model.nodes as Record<string, ModelNode>)
    .filter(node => isNumericConstantNode(node) && isNumericConstantExposed(node))
    .sort((a, b) => a.id - b.id)
    .map(node => ({
      nodeId: node.id,
      name: numericConstantDisplayName(node),
      value: numericConstantValueString(node),
    }))
}

export function isBarChartNode(node: ModelNode): boolean {
  return node.name === BAR_CHART_COMPONENT_NAME
}

export function isSankeyDiagramNode(node: ModelNode): boolean {
  return node.name === SANKEY_COMPONENT_NAME
}

export function datasetChartKindForNode(node: ModelNode): DatasetChartKind | null {
  if (node.name === PIE_CHART_COMPONENT_NAME) return "pie"
  if (node.name === BAR_DISTRIBUTION_CHART_COMPONENT_NAME) return "distribution"
  if (node.name === HISTOGRAM_CHART_COMPONENT_NAME) return "hist"
  return null
}

export function isDatasetChartNode(node: ModelNode): boolean {
  return datasetChartKindForNode(node) != null
}

export function isTableChartNode(node: ModelNode): boolean {
  return node.name === TABLE_CHART_COMPONENT_NAME
}

export function isTableCompareChartNode(node: ModelNode): boolean {
  return node.name === TABLE_COMPARE_CHART_COMPONENT_NAME
}

export function isSpiderChartNode(node: ModelNode): boolean {
  return node.name === SPIDER_CHART_COMPONENT_NAME
}

function chartDisplayName(node: ModelNode): string {
  const custom = node.data?.name
  if (typeof custom === "string" && custom.trim() !== "") {
    return custom
  }
  return node.name
}

function readBarChartVariables(value: unknown): BarChartVariable[] {
  if (!Array.isArray(value)) {
    return []
  }
  return value.flatMap(item => {
    if (!item || typeof item !== "object") {
      return []
    }
    const rec = item as { label?: unknown; value?: unknown }
    const label = typeof rec.label === "string" ? rec.label : String(rec.label ?? "")
    const num = typeof rec.value === "number" ? rec.value : Number(rec.value)
    if (!Number.isFinite(num)) {
      return []
    }
    return [{ label, value: num }]
  })
}

export type BarChartDetailEntry = {
  kind: "bar"
  nodeId: number
  name: string
  variables: BarChartVariable[]
}

export type SankeyChartDetailEntry = {
  kind: "sankey"
  nodeId: number
  name: string
  variables: SankeyStageTransition[]
}

export type DatasetChartDetailEntry = {
  kind: DatasetChartKind
  nodeId: number
  name: string
  chart: SerializedDatasetChart | null
}

export type TableChartDetailEntry = {
  kind: "table"
  nodeId: number
  name: string
  chart: SerializedTableChart | null
}

export type TableCompareChartDetailEntry = {
  kind: "tableCompare"
  nodeId: number
  name: string
  chart: SerializedTableCompareChart | null
}

export type SpiderChartDetailEntry = {
  kind: "spider"
  nodeId: number
  name: string
  chart: SerializedSpiderChart | null
}

export type ChartDetailEntry = BarChartDetailEntry | SankeyChartDetailEntry | DatasetChartDetailEntry | TableChartDetailEntry | TableCompareChartDetailEntry | SpiderChartDetailEntry

export function chartDetailIcon(kind: ChartDetailEntry["kind"]): string {
  switch (kind) {
    case "sankey":
      return "fa-project-diagram"
    case "pie":
      return "fa-chart-pie"
    case "hist":
      return "fa-chart-column"
    case "table":
      return "fa-table"
    case "tableCompare":
      return "fa-exchange-alt"
    case "spider":
      return "fa-spider"
    case "distribution":
    case "bar":
      return "fa-chart-bar"
  }
}

export function chartDetailKindLabel(kind: ChartDetailEntry["kind"]): string {
  switch (kind) {
    case "sankey":
      return "Sankey diagram"
    case "pie":
      return "Pie chart"
    case "hist":
      return "Histogram"
    case "table":
      return "Table chart"
    case "tableCompare":
      return "Table compare"
    case "spider":
      return "Spider diagram"
    case "distribution":
      return "Bar distribution"
    case "bar":
      return "Bar chart"
  }
}

export function chartEntryHasData(entry: ChartDetailEntry): boolean {
  switch (entry.kind) {
    case "pie":
    case "distribution":
    case "hist":
      return (entry.chart?.count.length ?? 0) > 0
    case "table":
      return (entry.chart?.rows.length ?? 0) > 0
    case "tableCompare":
      return entry.chart != null && tableCompareDifference(entry.chart, false).length > 0
    case "spider":
      return spiderChartHasData(entry.chart)
    case "bar":
    case "sankey":
      return entry.variables.length > 0
  }
}

function chartDetailEntryFromNode(node: ModelNode): ChartDetailEntry | null {
  if (isSankeyDiagramNode(node)) {
    return {
      kind: "sankey",
      nodeId: node.id,
      name: chartDisplayName(node),
      variables: deserializeSankeyVariables(node.data?.[SANKEY_VARIABLES_KEY]),
    }
  }
  if (isBarChartNode(node)) {
    return {
      kind: "bar",
      nodeId: node.id,
      name: chartDisplayName(node),
      variables: readBarChartVariables(node.data?.[BAR_CHART_VARIABLES_KEY]),
    }
  }
  const datasetKind = datasetChartKindForNode(node)
  if (datasetKind) {
    return {
      kind: datasetKind,
      nodeId: node.id,
      name: chartDisplayName(node),
      chart: readDatasetChart(node.data?.[DATASET_CHART_KEY]),
    }
  }
  if (isTableChartNode(node)) {
    return {
      kind: "table",
      nodeId: node.id,
      name: chartDisplayName(node),
      chart: readTableChart(node.data?.[TABLE_CHART_KEY]),
    }
  }
  if (isTableCompareChartNode(node)) {
    return {
      kind: "tableCompare",
      nodeId: node.id,
      name: chartDisplayName(node),
      chart: readTableCompareChart(node.data?.[TABLE_COMPARE_CHART_KEY]),
    }
  }
  if (isSpiderChartNode(node)) {
    return {
      kind: "spider",
      nodeId: node.id,
      name: chartDisplayName(node),
      chart: readSpiderChart(node.data?.[SPIDER_CHART_KEY]),
    }
  }
  return null
}

export function getChartDetailEntry(model: Data | null, nodeId: number | null): ChartDetailEntry | null {
  if (nodeId == null || model?.nodes === undefined) {
    return null
  }
  const nodes = model.nodes as Record<string, ModelNode>
  const node = nodes[String(nodeId)] ?? nodes[nodeId]
  if (!node) {
    return null
  }
  return chartDetailEntryFromNode(node)
}

export function getChartDetailEntries(model: Data | null): ChartDetailEntry[] {
  if (model?.nodes === undefined) {
    return []
  }

  return Object.values(model.nodes as Record<string, ModelNode>)
    .filter(node => (isBarChartNode(node) || isSankeyDiagramNode(node) || isDatasetChartNode(node) || isTableChartNode(node) || isTableCompareChartNode(node) || isSpiderChartNode(node)) && isNodeExposed(node))
    .sort((a, b) => a.id - b.id)
    .map(node => chartDetailEntryFromNode(node))
    .filter((entry): entry is ChartDetailEntry => entry != null)
}
